import { Link } from 'react-router-dom'
import type { DirectoryPlumberCard as PlumberCard } from '../../types'
import type { DirectoryExtraFilters } from '../../lib/api'
import { hasActiveFilters } from '../../lib/directoryConstants'
import { DirectoryFiltersBar } from './DirectoryFiltersBar'
import { DirectoryPlumberCard } from './DirectoryPlumberCard'
import { DirectoryLoadMoreSentinel } from './DirectoryLoadMoreSentinel'
import { PlumberCardListSkeleton } from '../ui/Skeletons'

interface Props {
  plumbers: PlumberCard[]
  isLoading: boolean
  fetchNextPage: () => void
  hasNextPage: boolean | undefined
  isFetchingNextPage: boolean
  listTotal: number | undefined
  extraFilters: DirectoryExtraFilters
  onFiltersChange: (f: DirectoryExtraFilters) => void
  hrefBuilder: (p: PlumberCard) => string
  /** Section heading rendered above the filters bar. */
  heading?: string
  /** Used in the empty state: "No plumbers found in {emptyAreaLabel}". */
  emptyAreaLabel?: string
  /** Optional back link shown in the empty state when no filters are active. */
  backLink?: { to: string; label: string }
}

export function DirectoryPlumberList({
  plumbers,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  listTotal,
  extraFilters,
  onFiltersChange,
  hrefBuilder,
  heading,
  emptyAreaLabel,
  backLink,
}: Props) {
  const filtersActive = hasActiveFilters(extraFilters)

  return (
    <div className="mt-8">
      {heading && (
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">{heading}</h2>
      )}
      <DirectoryFiltersBar
        filters={extraFilters}
        onChange={onFiltersChange}
        totalCount={isLoading ? undefined : listTotal}
      />

      {isLoading ? (
        <PlumberCardListSkeleton count={4} />
      ) : plumbers.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p>
            No plumbers found
            {filtersActive ? ' matching your filters' : emptyAreaLabel ? ` in ${emptyAreaLabel}` : ''}.
          </p>
          {filtersActive ? (
            <button
              onClick={() => onFiltersChange({})}
              className="text-trust-500 hover:underline text-sm mt-2 inline-block"
            >
              Clear filters
            </button>
          ) : backLink ? (
            <Link to={backLink.to} className="text-trust-500 hover:underline text-sm mt-2 inline-block">
              {backLink.label}
            </Link>
          ) : null}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {plumbers.map(p => (
              <DirectoryPlumberCard key={p._id} plumber={p} href={hrefBuilder(p)} />
            ))}
          </div>
          <DirectoryLoadMoreSentinel
            onLoadMore={() => { void fetchNextPage() }}
            active={hasNextPage === true && !isFetchingNextPage}
          />
          {isFetchingNextPage && (
            <p className="text-center text-sm text-gray-400 py-4">Loading more…</p>
          )}
        </>
      )}

    </div>
  )
}
